// ============================================================
// Sales CRM — FCM Push Registration Hook
// ============================================================
import { useEffect, useRef } from "react";
import API from "./api";

const FIREBASE_CONFIG = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const FIREBASE_READY = !!(
  FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.projectId && VAPID_KEY
);

const ICON = "/icon-ebek-one-512.png";

let firebaseMsg    = null;
let swRegistration = null;

// iOS/iPadOS gained web push in 16.4, but ONLY for a PWA added to the Home
// Screen. In a normal Safari tab the APIs exist yet can never produce a
// working subscription, so detect that and skip rather than prompting for a
// permission that cannot work.
function isIos() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isInstalledPwa() {
  return window.matchMedia?.("(display-mode: standalone)").matches === true
    || window.navigator.standalone === true;
}

export function pushSupported() {
  if (!FIREBASE_READY) return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  if (isIos() && !isInstalledPwa()) return false;
  return true;
}

async function initFirebase() {
  if (!FIREBASE_READY) return null;
  if (firebaseMsg) return firebaseMsg;
  try {
    const { initializeApp, getApps } = await import("firebase/app");
    const { getMessaging, isSupported } = await import("firebase/messaging");
    if (!await isSupported()) return null;
    const app = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
    firebaseMsg = getMessaging(app);
    return firebaseMsg;
  } catch (err) {
    console.warn("[FCM] Firebase init error:", err.message);
    return null;
  }
}

async function registerSW() {
  if (swRegistration) return swRegistration;
  if (!("serviceWorker" in navigator)) return null;
  try {
    // Give the FCM worker its own scope. /sw.js already owns the root
    // scope for offline caching, and two register() calls at the same
    // scope overwrite each other's registration — which silently destroys
    // whichever push subscription was created first.
    swRegistration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      { scope: "/firebase-cloud-messaging-push-scope" }
    );
    return swRegistration;
  } catch (err) {
    console.warn("[FCM] SW registration failed:", err.message);
    return null;
  }
}

// Assumes permission has already been granted.
async function registerToken(authToken) {
  try {
    const [messaging, registration] = await Promise.all([initFirebase(), registerSW()]);
    if (!messaging || !registration) return;

    const { getToken } = await import("firebase/messaging");
    const fcmToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!fcmToken) return;

    localStorage.setItem("fcm_token", fcmToken);
    await API.post("/notifications/fcm-token",
      { token: fcmToken, deviceInfo: navigator.userAgent.substring(0, 200) },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
  } catch (err) {
    console.warn("[FCM] Token registration error:", err.message);
  }
}

// Safari (iOS especially) only honours Notification.requestPermission() while
// a user gesture is still "active". Any await before the call spends that
// activation, so the request must be the very first statement in the handler.
function requestPermissionNow() {
  try {
    return Promise.resolve(Notification.requestPermission());
  } catch (err) {
    console.warn("[FCM] Permission request failed:", err.message);
    return Promise.resolve("denied");
  }
}

// Prompt on the user's next interaction. This satisfies Safari's
// user-activation requirement without needing a dedicated button, and on
// Chrome it also avoids the "quiet" heuristic that suppresses prompts made
// on page load.
function promptOnNextGesture(authToken) {
  const events = ["click", "touchend", "keydown"];
  let fired = false;
  const handler = () => {
    if (fired) return;
    fired = true;
    events.forEach((name) => document.removeEventListener(name, handler, true));
    requestPermissionNow().then((permission) => {
      if (permission === "granted") registerToken(authToken);
    });
  };
  events.forEach((name) => document.addEventListener(name, handler, true));
}

function showForeground(title, body) {
  // Android Chrome does not support the Notification constructor — it throws
  // "Illegal constructor". Go through the SW registration, which works on
  // both mobile and desktop.
  navigator.serviceWorker.ready
    .then((reg) => reg.showNotification(title, { body, icon: ICON }))
    .catch(() => {
      try { new Notification(title, { body, icon: ICON }); } catch { /* unsupported */ }
    });
}

/**
 * Trigger the permission prompt from a user gesture (e.g. a button onClick).
 * Call it directly from the handler — do not await anything beforehand, or
 * Safari will have already discarded the user activation.
 */
export async function enablePush(user, authToken) {
  if (!user || !authToken) return { ok: false, reason: "not-logged-in" };
  if (!pushSupported()) {
    return {
      ok: false,
      reason: isIos() && !isInstalledPwa() ? "ios-needs-install" : "unsupported",
    };
  }
  const permission = await requestPermissionNow();
  if (permission !== "granted") return { ok: false, reason: permission || "denied" };
  await registerToken(authToken);
  return { ok: true };
}

export function useFcmPush(user, token) {
  const started = useRef(false);

  useEffect(() => {
    if (!user || !token || started.current || !pushSupported()) return;
    started.current = true;

    if (Notification.permission === "granted") {
      registerToken(token);
    } else if (Notification.permission === "default") {
      promptOnNextGesture(token);
    }
    // "denied" — only the user can undo this, in browser/OS settings

    (async () => {
      const messaging = await initFirebase();
      if (!messaging) return;
      const { onMessage } = await import("firebase/messaging");
      onMessage(messaging, (payload) => {
        const { title, body } = payload.notification || {};
        if (title && Notification.permission === "granted") showForeground(title, body);
      });
    })();
  }, [user, token]);
}

export async function unregisterFcmToken(token) {
  const fcmToken = localStorage.getItem("fcm_token");
  if (!fcmToken || !token) return;
  try {
    await API.delete("/notifications/fcm-token",
      { data: { token: fcmToken }, headers: { Authorization: `Bearer ${token}` } }
    );
    localStorage.removeItem("fcm_token");
  } catch { /* silent */ }
}
